import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { db, functions } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../ToastContext';
import { fmtTokens } from '../../utils/helpers';

// ─────────────────────────────────────────────────────────────────────────────
// CLOUD FUNCTION REFERENCES (module-level)
// ─────────────────────────────────────────────────────────────────────────────
const bjJoinTableFn = httpsCallable(functions, 'bjJoinTable');
const bjLeaveTableFn = httpsCallable(functions, 'bjLeaveTable');
const bjPlaceBetFn = httpsCallable(functions, 'bjPlaceBet');
const bjActionFn = httpsCallable(functions, 'bjAction');
const bjAdvanceFn = httpsCallable(functions, 'bjAdvance');

// ─────────────────────────────────────────────────────────────────────────────
// WEB AUDIO — synthesised card / chip sounds
// ─────────────────────────────────────────────────────────────────────────────
class BlackjackAudio {
  constructor() { this.ctx = null; this.muted = false; }

  init() {
    if (!this.ctx) this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  // Soft card slide — gentle filtered noise + quiet thud
  cardDeal() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    const bufSize = this.ctx.sampleRate * 0.08;
    const buf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / bufSize);
    const src = this.ctx.createBufferSource(); src.buffer = buf;
    const filt = this.ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.frequency.value = 2000; filt.Q.value = 0.5;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.04, now); g.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    src.connect(filt).connect(g).connect(this.ctx.destination);
    src.start(); src.stop(now + 0.08);
    // Soft thud
    const o = this.ctx.createOscillator(); const og = this.ctx.createGain();
    o.type = 'sine'; o.frequency.value = 180;
    o.frequency.exponentialRampToValueAtTime(80, now + 0.06);
    og.gain.setValueAtTime(0.03, now); og.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    o.connect(og).connect(this.ctx.destination); o.start(); o.stop(now + 0.06);
  }

  // Soft chip clink — two gentle high tones
  chipPlace() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    [2200, 2800].forEach((f, i) => {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'sine'; o.frequency.value = f;
      g.gain.setValueAtTime(0.025, now + i * 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.04 + 0.08);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.04); o.stop(now + i * 0.04 + 0.08);
    });
  }

  // Pleasant ascending chime
  win() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    [523, 659, 784].forEach((freq, i) => {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'sine'; o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.15);
      g.gain.linearRampToValueAtTime(0.06, now + i * 0.15 + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.4);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.4);
    });
  }

  // Triumphant fanfare — warm triangle wave
  blackjack() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    [523, 659, 784, 1047].forEach((freq, i) => {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'triangle'; o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.12);
      g.gain.linearRampToValueAtTime(0.07, now + i * 0.12 + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.5);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.5);
    });
  }

  // Gentle descending tone — not harsh
  bust() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
    o.type = 'sine'; o.frequency.value = 300;
    o.frequency.exponentialRampToValueAtTime(120, now + 0.35);
    g.gain.setValueAtTime(0.06, now); g.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    o.connect(g).connect(this.ctx.destination); o.start(); o.stop(now + 0.35);
  }

  // Soft tick warning — not jarring
  timerWarn() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
    o.type = 'sine'; o.frequency.value = 660;
    g.gain.setValueAtTime(0.035, now); g.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    o.connect(g).connect(this.ctx.destination); o.start(); o.stop(now + 0.08);
  }
}

const audio = new BlackjackAudio();

// ─────────────────────────────────────────────────────────────────────────────
// CARD RENDERING HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const SUIT_SYMBOLS = { hearts: '♥', diamonds: '♦', clubs: '♣', spades: '♠' };
const RED_SUITS = ['hearts', 'diamonds'];

function CardView({ card, faceDown, small, style: extraStyle }) {
  const w = small ? 46 : 62;
  const h = small ? 66 : 88;
  if (faceDown || !card || card.rank === '?') {
    return (
      <div style={{
        width: w, height: h, borderRadius: 7, flexShrink: 0,
        background: 'linear-gradient(145deg, #2a3f8f 0%, #1a2860 50%, #0f1a40 100%)',
        border: '1.5px solid rgba(80,120,255,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: small ? 14 : 18, color: 'rgba(100,140,255,0.4)',
        boxShadow: '0 2px 10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
        ...extraStyle,
      }}>
        <div style={{ width: w - 10, height: h - 10, borderRadius: 4,
          border: '1px solid rgba(80,120,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>♠</div>
      </div>
    );
  }
  const isRed = RED_SUITS.includes(card.suit);
  return (
    <div style={{
      width: w, height: h, borderRadius: 7, flexShrink: 0,
      background: 'linear-gradient(180deg, #f8f8f8 0%, #e4e4e4 100%)',
      border: '1.5px solid rgba(200,200,200,0.6)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      color: isRed ? '#c41230' : '#1a1a1a',
      fontSize: small ? 13 : 16, fontWeight: '800', lineHeight: 1.1,
      fontFamily: 'Georgia, serif',
      boxShadow: '0 2px 10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.8)',
      ...extraStyle,
    }}>
      <div style={{ fontSize: small ? 15 : 19, fontWeight: '900' }}>{card.rank}</div>
      <div style={{ fontSize: small ? 13 : 16, lineHeight: 1 }}>{SUIT_SYMBOLS[card.suit] || card.suit}</div>
    </div>
  );
}

function HandDisplay({ cards, small }) {
  if (!cards || cards.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {cards.map((c, i) => (
        <CardView key={i} card={c} small={small} style={{ marginLeft: i > 0 ? (small ? -20 : -28) : 0, zIndex: i }} />
      ))}
    </div>
  );
}

function handValueDisplay(cards) {
  if (!cards || cards.length === 0) return '';
  let total = 0, aces = 0;
  for (const c of cards) {
    if (!c || c.rank === '?') return '?';
    if (c.rank === 'A') { aces++; total += 11; }
    else if (['K', 'Q', 'J'].includes(c.rank)) total += 10;
    else total += parseInt(c.rank, 10);
  }
  let softAces = aces;
  while (total > 21 && softAces > 0) { total -= 10; softAces--; }
  // Show soft/hard like "7/17" when an ace is still counted as 11
  if (softAces > 0 && total !== 21) {
    const hard = total - 10;
    return `${hard}/${total}`;
  }
  return total;
}

// Returns the numeric best-value of a hand (for comparisons like > 21)
function handValueNumeric(cards) {
  const v = handValueDisplay(cards);
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.includes('/')) return parseInt(v.split('/')[1], 10);
  return NaN;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const BJ_MAX_SEATS = 7;
const BJ_ACCENT = '#4169E1';
const BJ_ACCENT_DIM = 'rgba(65,105,225,0.15)';
const BJ_ACCENT_BORDER = 'rgba(65,105,225,0.5)';

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function BlackjackPage({ user, userData, onUserDataChange, onTokenAdjustmentChange, onBack, isMobile }) {
  const toast = useToast();

  // ── State ──────────────────────────────────────────────────────────────────
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState(false);
  const [muted, setMuted] = useState(false);
  const [betAmount, setBetAmount] = useState(1);
  const [tokenType, setTokenType] = useState('crewmate');
  const [bettingTimeLeft, setBettingTimeLeft] = useState(0);
  const [turnTimeLeft, setTurnTimeLeft] = useState(0);
  const [resultShown, setResultShown] = useState(false);
  const [dealerRevealDone, setDealerRevealDone] = useState(false);

  const tokenAdjRef = useRef({ crewmate: 0, imposter: 0 });
  const advanceTimerRef = useRef(null);
  const prevPhaseRef = useRef(null);
  const prevRoundRef = useRef(null);
  const timerWarnPlayedRef = useRef(false);
  const optimisticRef = useRef(null);

  // Dealer card reveal animation
  const [revealedDealerCards, setRevealedDealerCards] = useState([]);
  const prevDealerCardsRef = useRef('[]');
  const dealerRevealTimeoutsRef = useRef([]);

  // ── Audio init ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleInit = () => { audio.init(); document.removeEventListener('click', handleInit); };
    document.addEventListener('click', handleInit);
    return () => document.removeEventListener('click', handleInit);
  }, []);
  useEffect(() => { audio.muted = muted; }, [muted]);

  // ── Derived state ─────────────────────────────────────────────────────────
  const uid = user?.uid;
  const seats = table?.seats || new Array(BJ_MAX_SEATS).fill(null);
  const phase = table?.phase || 'waiting';
  const mySeatIdx = useMemo(() => seats.findIndex(s => s && s.userId === uid), [seats, uid]);
  const isJoined = mySeatIdx >= 0;
  const mySeat = isJoined ? seats[mySeatIdx] : null;
  const myHands = isJoined && table?.hands ? table.hands[mySeatIdx] : null;
  const isMyTurn = phase === 'playing' && table?.currentSeat === mySeatIdx;
  const myBet = table?.bets?.[mySeatIdx];
  const hasBet = myBet !== undefined && myBet !== null;

  const balance = useMemo(() => {
    const t = isJoined && mySeat ? mySeat.tokenType : tokenType;
    const base = t === 'crewmate'
      ? (userData?.crewmateTokens || 0) + tokenAdjRef.current.crewmate
      : (userData?.imposterTokens || 0) + tokenAdjRef.current.imposter;
    return Math.max(0, base);
  }, [userData, tokenType, mySeat, isJoined]);

  // ── Real-time listener ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    const tableRef = doc(db, 'casino', 'blackjackTable');
    const unsub = onSnapshot(tableRef, (snap) => {
      if (snap.exists()) {
        const s = snap.data();
        // Clear stale optimistic state (same pattern as poker)
        if (optimisticRef.current) {
          const age = Date.now() - optimisticRef.current.appliedAt;
          if (age > 3000 || s.currentSeat !== optimisticRef.current.snapshot?.currentSeat
              || s.roundNumber !== optimisticRef.current.snapshot?.roundNumber) {
            optimisticRef.current = null;
          }
        }
        setTable(s);
      } else {
        setTable(null);
      }
      setLoading(false);
    }, (err) => {
      console.error('Blackjack listener error:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [uid]);

  // ── Phase change effects (SFX, result overlay) ────────────────────────────
  useEffect(() => {
    if (!table) return;
    const prevPhase = prevPhaseRef.current;
    const prevRound = prevRoundRef.current;
    prevPhaseRef.current = phase;
    prevRoundRef.current = table.roundNumber;

    // New round dealt
    if (phase === 'playing' && prevPhase !== 'playing' && table.roundNumber !== prevRound) {
      audio.cardDeal();
      setTimeout(() => audio.cardDeal(), 200);
      setTimeout(() => audio.cardDeal(), 400);
    }

    // Round completed — immediately block results until dealer cards are revealed
    if (phase === 'complete' && prevPhase !== 'complete') {
      setDealerRevealDone(false);
      setResultShown(false);
      // Flush token adjustment on complete
      if (tokenAdjRef.current.crewmate !== 0 || tokenAdjRef.current.imposter !== 0) {
        tokenAdjRef.current = { crewmate: 0, imposter: 0 };
        if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
        if (onUserDataChange) onUserDataChange();
      }
    }

    // Phase changed from complete → betting
    if (phase === 'betting' && prevPhase === 'complete') {
      setResultShown(false);
    }
  }, [table, phase, isJoined, mySeatIdx, onTokenAdjustmentChange, onUserDataChange]);

  // ── Show results AFTER dealer reveal completes ──────────────────────────
  useEffect(() => {
    if (phase !== 'complete' || resultShown) return;
    // Double-check: dealerRevealDone flag AND actual revealed cards match dealer cards
    const dealerCards = table?.dealerCards || [];
    const allRevealed = dealerRevealDone && revealedDealerCards.length >= dealerCards.length && dealerCards.length > 0;
    if (!allRevealed) return;

    setResultShown(true);
    // Play sound based on my result
    if (isJoined && table?.results?.[mySeatIdx]) {
      const myResult = table.results[mySeatIdx];
      const hasBlackjack = myResult.outcomes?.includes('blackjack');
      const hasWin = myResult.outcomes?.includes('win');
      const allBust = myResult.outcomes?.every(o => o === 'bust');
      if (hasBlackjack) audio.blackjack();
      else if (hasWin) audio.win();
      else if (allBust) audio.bust();
    }
  }, [phase, dealerRevealDone, revealedDealerCards, resultShown, isJoined, mySeatIdx, table]);

  // ── Heartbeat (bjAdvance) — poll for timeouts and round transitions ───────
  useEffect(() => {
    if (!uid || !table) return;
    if (advanceTimerRef.current) clearInterval(advanceTimerRef.current);

    advanceTimerRef.current = setInterval(() => {
      if (phase === 'waiting') return;
      bjAdvanceFn().catch(() => {});
    }, 2000);

    return () => {
      if (advanceTimerRef.current) clearInterval(advanceTimerRef.current);
    };
  }, [uid, phase, table?.roundNumber]);

  // ── Betting timer countdown ────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'betting' || !table?.bettingDeadline) {
      setBettingTimeLeft(0);
      return;
    }
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((table.bettingDeadline - Date.now()) / 1000));
      setBettingTimeLeft(remaining);
    }, 200);
    return () => clearInterval(interval);
  }, [phase, table?.bettingDeadline]);

  // ── Turn timer countdown ──────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'playing' || !table?.turnDeadline) {
      setTurnTimeLeft(0);
      timerWarnPlayedRef.current = false;
      return;
    }
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((table.turnDeadline - Date.now()) / 1000));
      setTurnTimeLeft(remaining);
      if (remaining <= 5 && remaining > 0 && isMyTurn && !timerWarnPlayedRef.current) {
        audio.timerWarn();
        timerWarnPlayedRef.current = true;
      }
      if (remaining > 5) timerWarnPlayedRef.current = false;
    }, 200);
    return () => clearInterval(interval);
  }, [phase, table?.turnDeadline, isMyTurn]);

  // ── Dealer card reveal animation ─────────────────────────────────────────
  useEffect(() => {
    const newCards = table?.dealerCards || [];
    const newKey = JSON.stringify(newCards);
    const prevKey = prevDealerCardsRef.current;

    if (newKey === prevKey) return; // No change

    const prevCards = JSON.parse(prevKey);
    prevDealerCardsRef.current = newKey;

    // Clean up existing reveal timeouts
    dealerRevealTimeoutsRef.current.forEach(t => clearTimeout(t));
    dealerRevealTimeoutsRef.current = [];

    if (newCards.length <= prevCards.length || newCards.length <= 1) {
      // Fewer/same cards or just the initial up card — show all immediately
      setRevealedDealerCards(newCards);
      setDealerRevealDone(true);
      return;
    }

    // New cards being revealed (hole card + draws) — stagger them
    setRevealedDealerCards(prevCards);
    setDealerRevealDone(false);
    const newCount = newCards.length - prevCards.length;

    for (let i = prevCards.length; i < newCards.length; i++) {
      const cardsToShow = newCards.slice(0, i + 1);
      const isLast = i === newCards.length - 1;
      const timeout = setTimeout(() => {
        audio.cardDeal();
        setRevealedDealerCards(cardsToShow);
        if (isLast) setDealerRevealDone(true);
      }, (i - prevCards.length + 1) * 600);
      dealerRevealTimeoutsRef.current.push(timeout);
    }

    return () => {
      dealerRevealTimeoutsRef.current.forEach(t => clearTimeout(t));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.dealerCards && JSON.stringify(table.dealerCards)]);

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleJoin = useCallback(async (seatIndex) => {
    if (actionPending) return;
    setActionPending(true);
    try {
      await bjJoinTableFn({ tokenType, seatIndex });
      toast.success('Joined the blackjack table!');
      if (onUserDataChange) onUserDataChange();
    } catch (err) {
      toast.error(err?.message || 'Failed to join');
    } finally {
      setActionPending(false);
    }
  }, [actionPending, tokenType, toast, onUserDataChange]);

  const handleLeave = useCallback(async () => {
    if (actionPending) return;
    setActionPending(true);
    try {
      await bjLeaveTableFn();
      toast.success('Left the table');
      tokenAdjRef.current = { crewmate: 0, imposter: 0 };
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
      if (onUserDataChange) onUserDataChange();
    } catch (err) {
      toast.error(err?.message || 'Failed to leave');
    } finally {
      setActionPending(false);
    }
  }, [actionPending, toast, onUserDataChange, onTokenAdjustmentChange]);

  const handlePlaceBet = useCallback(() => {
    if (actionPending || hasBet) return;
    const bet = parseFloat(parseFloat(betAmount).toFixed(2));
    if (!Number.isFinite(bet) || bet < 0.1) { toast.error('Minimum bet is 0.1'); return; }
    if (bet > balance) { toast.error('Insufficient balance'); return; }

    setActionPending(true);
    audio.chipPlace();

    // Optimistic token deduction
    const effTokenType = mySeat?.tokenType || tokenType;
    if (effTokenType === 'crewmate') tokenAdjRef.current.crewmate -= bet;
    else tokenAdjRef.current.imposter -= bet;
    // Nav subtracts tokenAdjustment, so invert sign for correct display
    if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: -tokenAdjRef.current.crewmate, imposter: -tokenAdjRef.current.imposter, frozen: true });

    // Optimistic bet display in seat
    setTable(prev => {
      if (!prev) return prev;
      const updated = JSON.parse(JSON.stringify(prev));
      if (!updated.bets) updated.bets = {};
      updated.bets[mySeatIdx] = bet;
      return updated;
    });

    // Fire-and-forget — rely on onSnapshot for authoritative state
    bjPlaceBetFn({ betAmount: bet }).catch(err => {
      // Revert optimistic token + bet
      if (effTokenType === 'crewmate') tokenAdjRef.current.crewmate += bet;
      else tokenAdjRef.current.imposter += bet;
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: -tokenAdjRef.current.crewmate, imposter: -tokenAdjRef.current.imposter, frozen: false });
      setTable(prev => {
        if (!prev) return prev;
        const updated = JSON.parse(JSON.stringify(prev));
        delete updated.bets?.[mySeatIdx];
        return updated;
      });
      toast.error(err?.message || 'Failed to place bet');
    });

    // Short debounce to prevent double-clicks, then release
    setTimeout(() => setActionPending(false), 400);
  }, [actionPending, hasBet, betAmount, balance, mySeat, mySeatIdx, tokenType, toast, onTokenAdjustmentChange]);

  const handleAction = useCallback((action) => {
    if (actionPending || !isMyTurn) return;
    setActionPending(true);

    // Play sound immediately
    if (action === 'hit') audio.cardDeal();
    else if (action === 'stand') audio.chipPlace();
    else if (action === 'double') { audio.chipPlace(); audio.cardDeal(); }
    else if (action === 'split') audio.chipPlace();

    // Capture extra bet before optimistic update (for double/split revert)
    let extraBet = 0;
    let effTokenType = mySeat?.tokenType || tokenType;
    if (action === 'double' || action === 'split') {
      const hand = myHands?.[table.currentHandIndex];
      if (hand) extraBet = hand.bet;
    }

    // Optimistic token deduction for double/split
    if (extraBet > 0) {
      if (effTokenType === 'crewmate') tokenAdjRef.current.crewmate -= extraBet;
      else tokenAdjRef.current.imposter -= extraBet;
      // Nav subtracts tokenAdjustment, so invert sign for correct display
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: -tokenAdjRef.current.crewmate, imposter: -tokenAdjRef.current.imposter, frozen: true });
    }

    // Apply optimistic state update (same pattern as poker)
    setTable(prev => {
      if (!prev) return prev;
      const updated = JSON.parse(JSON.stringify(prev));
      const hands = updated.hands?.[mySeatIdx];
      if (!hands) return prev;
      const hand = hands[updated.currentHandIndex];
      if (!hand) return prev;

      optimisticRef.current = { action, appliedAt: Date.now(), snapshot: prev };

      if (action === 'stand') {
        hand.standing = true;
      } else if (action === 'double') {
        hand.doubled = true;
        hand.standing = true;
        if (updated.bets?.[mySeatIdx] !== undefined) {
          updated.bets[mySeatIdx] = (updated.bets[mySeatIdx] || 0) + hand.bet;
        }
      }
      // hit/split: can't fake the new card, but snapshot is saved for rollback

      return updated;
    });

    // Fire-and-forget — rely on onSnapshot for authoritative state
    bjActionFn({ action }).catch(err => {
      // Revert optimistic state
      if (optimisticRef.current?.snapshot) {
        setTable(optimisticRef.current.snapshot);
      }
      optimisticRef.current = null;
      // Revert token adj for double/split
      if (extraBet > 0) {
        if (effTokenType === 'crewmate') tokenAdjRef.current.crewmate += extraBet;
        else tokenAdjRef.current.imposter += extraBet;
        if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: -tokenAdjRef.current.crewmate, imposter: -tokenAdjRef.current.imposter, frozen: false });
      }
      toast.error(err?.message || 'Action failed');
    });

    // Short debounce to prevent double-clicks, then release
    setTimeout(() => setActionPending(false), 400);
  }, [actionPending, isMyTurn, myHands, mySeatIdx, table, mySeat, tokenType, toast, onTokenAdjustmentChange]);

  // ── Compute action availability ────────────────────────────────────────────
  const currentHand = isMyTurn && myHands ? myHands[table.currentHandIndex] : null;
  const canHit = isMyTurn && currentHand && !currentHand.standing && !currentHand.doubled && !currentHand.isSplitAces;
  const canStand = isMyTurn && currentHand && !currentHand.standing;
  const canDouble = isMyTurn && currentHand && currentHand.cards?.length === 2 && !currentHand.standing && !currentHand.doubled && !currentHand.isSplitAces && balance >= (currentHand?.bet || 0);
  const canSplit = isMyTurn && currentHand && currentHand.cards?.length === 2 && !currentHand.standing
    && currentHand.cards[0]?.rank === currentHand.cards[1]?.rank
    && (!myHands || myHands.length < 4)
    && balance >= (currentHand?.bet || 0);

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = useMemo(() => ({
    page: {
      position: isMobile ? 'relative' : 'fixed',
      top: isMobile ? 0 : 0, left: isMobile ? undefined : 'var(--sidebar-w)',
      right: isMobile ? undefined : 0, bottom: isMobile ? undefined : 0,
      display: 'flex', flexDirection: 'column',
      background: 'var(--bg-primary)', overflow: isMobile ? 'visible' : 'hidden',
      minHeight: isMobile ? 'calc(100vh - 48px - 60px)' : undefined,
      paddingBottom: isMobile ? '20px' : 0,
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isMobile ? '8px 12px' : '12px 20px', flexShrink: 0,
      gap: 8, flexWrap: isMobile ? 'wrap' : undefined,
    },
    backBtn: {
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 12, padding: isMobile ? '8px 12px' : '8px 16px', color: '#aaa',
      cursor: 'pointer', fontSize: isMobile ? 12 : 13, fontWeight: 600,
    },
    muteBtn: {
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 12, padding: '8px 12px', cursor: 'pointer', fontSize: 16, color: '#aaa',
    },
    feltArea: {
      flex: 1, display: 'flex', flexDirection: 'column',
      padding: isMobile ? '0 8px 8px' : '0 20px 20px',
      minHeight: 0, overflow: isMobile ? 'visible' : 'auto',
      gap: isMobile ? 10 : 16,
    },
    dealerArea: {
      background: 'linear-gradient(180deg, rgba(0,60,0,0.25) 0%, rgba(0,40,0,0.15) 50%, rgba(0,20,0,0.2) 100%)',
      border: '1px solid rgba(0,180,0,0.15)', borderRadius: 16,
      padding: isMobile ? '14px 12px' : '20px 24px',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03), 0 4px 20px rgba(0,0,0,0.2)',
    },
    seatsRow: {
      display: 'flex', gap: isMobile ? 6 : 10,
      justifyContent: 'center', flexWrap: 'wrap',
    },
    seatCard: (occupied, isMe, isActive) => ({
      width: isMobile ? 'calc(50% - 6px)' : 140,
      minHeight: isMobile ? 130 : 170,
      background: isMe
        ? 'linear-gradient(180deg, rgba(65,105,225,0.12) 0%, rgba(20,30,60,0.6) 100%)'
        : occupied
          ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(10,10,15,0.6) 100%)'
          : 'rgba(255,255,255,0.015)',
      border: isActive
        ? '2px solid rgba(255,215,0,0.7)'
        : isMe
          ? `1.5px solid rgba(65,105,225,0.4)`
          : '1px solid rgba(255,255,255,0.06)',
      borderRadius: 14, padding: isMobile ? '8px 6px' : '10px 8px',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 5, position: 'relative',
      cursor: !occupied && !isJoined ? 'pointer' : 'default',
      transition: 'all 0.3s ease',
      boxShadow: isActive
        ? '0 0 20px rgba(255,215,0,0.15), inset 0 1px 0 rgba(255,255,255,0.05)'
        : isMe
          ? '0 2px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)'
          : '0 1px 6px rgba(0,0,0,0.2)',
    }),
    actionBtn: (color, disabled) => ({
      padding: isMobile ? '12px 16px' : '10px 20px',
      borderRadius: 10, cursor: disabled ? 'default' : 'pointer',
      fontSize: isMobile ? 13 : 14, fontWeight: 700, letterSpacing: '0.5px',
      background: disabled ? 'rgba(255,255,255,0.03)' : `${color}22`,
      border: `1.5px solid ${disabled ? 'rgba(255,255,255,0.06)' : color + '66'}`,
      color: disabled ? '#444' : color,
      opacity: disabled ? 0.4 : 1,
      transition: 'all 0.2s', minWidth: isMobile ? 0 : 80,
      flex: isMobile ? 1 : undefined,
    }),
    betInput: {
      background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(65,105,225,0.3)',
      borderRadius: 10, padding: '8px 12px', color: '#fff',
      fontSize: isMobile ? 16 : 14, width: isMobile ? 80 : 100,
      textAlign: 'center', fontFamily: 'Orbitron, monospace',
    },
    quickBet: {
      padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
      fontSize: 11, fontWeight: 600, color: '#bbb',
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
      transition: 'all 0.2s',
    },
    tokenToggle: (active) => ({
      padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
      fontSize: 12, fontWeight: 700, letterSpacing: '1px',
      background: active ? `${BJ_ACCENT_DIM}` : 'rgba(255,255,255,0.05)',
      border: active ? `1px solid ${BJ_ACCENT_BORDER}` : '1px solid rgba(255,255,255,0.1)',
      color: active ? '#fff' : '#888', transition: 'all 0.2s',
    }),
    phaseBar: {
      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10,
      padding: '8px 18px', borderRadius: 10,
      background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.06)',
      fontSize: 13, fontWeight: 700, color: '#bbb', letterSpacing: '1px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
  }), [isMobile, isJoined]);

  // ── Loading / not logged in ────────────────────────────────────────────────
  if (!user || !userData) {
    return <div style={S.page}><p style={{ padding: 20, color: '#aaa' }}>Please log in to play blackjack.</p></div>;
  }

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <button onClick={onBack} style={S.backBtn}>← Casino</button>
          <span style={{ color: '#FFD700', fontWeight: 800, fontSize: isMobile ? 16 : 20, letterSpacing: 2 }}>🃏 BLACKJACK</span>
          <div />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading...</div>
      </div>
    );
  }

  // ── Build seat data for rendering ──────────────────────────────────────────
  const bettingSeats = table?.bets ? Object.keys(table.bets).map(Number) : [];
  const dealerCards = table?.dealerCards || [];
  const dealerTotal = table?.dealerTotal || 0;
  const results = table?.results || {};

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}>← Casino</button>
        <span style={{ color: '#FFD700', fontWeight: 800, fontSize: isMobile ? 16 : 20, letterSpacing: 2 }}>🃏 BLACKJACK</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isJoined && (
            <div style={{ fontSize: 12, color: BJ_ACCENT, fontWeight: 700 }}>
              {mySeat?.tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(balance)}
            </div>
          )}
          <button onClick={() => setMuted(!muted)} style={S.muteBtn}>{muted ? '🔇' : '🔊'}</button>
          {isJoined && phase !== 'playing' && (
            <button onClick={handleLeave} disabled={actionPending} style={{
              ...S.backBtn, color: '#E8384A', borderColor: 'rgba(232,56,74,0.3)',
            }}>Leave</button>
          )}
        </div>
      </div>

      {/* Phase Status Bar */}
      <div style={{ padding: isMobile ? '0 8px 4px' : '0 20px 8px' }}>
        <div style={S.phaseBar}>
          {phase === 'waiting' && 'Waiting for players...'}
          {phase === 'betting' && (
            <>
              <span>PLACE YOUR BETS</span>
              <span style={{
                color: bettingTimeLeft <= 5 ? '#E8384A' : '#FFD700',
                fontFamily: 'Orbitron, monospace', fontSize: 16,
              }}>{bettingTimeLeft}s</span>
            </>
          )}
          {phase === 'playing' && (
            <>
              <span style={{ color: isMyTurn ? '#4ADE80' : '#bbb' }}>
                {isMyTurn ? 'YOUR TURN' : (table?.currentSeat >= 0 && seats[table.currentSeat]
                  ? `${seats[table.currentSeat].username}'s turn`
                  : 'Playing...')}
              </span>
              <span style={{
                color: turnTimeLeft <= 5 ? '#E8384A' : '#FFD700',
                fontFamily: 'Orbitron, monospace', fontSize: 16,
              }}>{turnTimeLeft}s</span>
            </>
          )}
          {phase === 'complete' && 'ROUND COMPLETE'}
        </div>
      </div>

      <div style={S.feltArea}>
        {/* Dealer Area */}
        <div style={S.dealerArea}>
          <div style={{ fontSize: 10, color: 'rgba(180,180,180,0.6)', fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase' }}>DEALER</div>
          {revealedDealerCards.length > 0 ? (
            <>
              <HandDisplay cards={revealedDealerCards} small={isMobile} />
              <div style={{
                fontSize: isMobile ? 16 : 20, fontWeight: 800,
                color: (table?.dealerBusted && revealedDealerCards.length === dealerCards.length) ? '#E8384A' : '#fff',
              }}>
                {(table?.dealerBusted && revealedDealerCards.length === dealerCards.length) ? 'BUST' : (() => {
                  const v = handValueDisplay(revealedDealerCards);
                  return v === '?' ? '?' : v;
                })()}
              </div>
            </>
          ) : dealerCards.length > 0 ? (
            <div style={{ fontSize: 13, color: '#888', padding: '16px 0' }}>Revealing...</div>
          ) : (
            <div style={{ fontSize: 13, color: '#555', padding: '16px 0' }}>No cards dealt</div>
          )}
        </div>

        {/* Player Seats */}
        <div style={S.seatsRow}>
          {seats.map((seat, idx) => {
            const occupied = seat !== null;
            const isMe = occupied && seat.userId === uid;
            const isActive = phase === 'playing' && table?.currentSeat === idx;
            const seatHands = table?.hands?.[idx];
            const seatBet = table?.bets?.[idx];
            const seatResult = results[idx];
            const isActiveHand = isActive && table?.currentHandIndex !== undefined;

            return (
              <div
                key={idx}
                style={S.seatCard(occupied, isMe, isActive)}
                onClick={() => {
                  if (!occupied && !isJoined && !actionPending) handleJoin(idx);
                }}
              >
                {/* Seat label */}
                <div style={{
                  fontSize: 9, fontWeight: 700, color: '#555', letterSpacing: 1.5,
                  position: 'absolute', top: 4, left: 8,
                }}>#{idx + 1}</div>

                {!occupied ? (
                  <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column', gap: 4,
                  }}>
                    {!isJoined ? (
                      <>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          border: '1.5px dashed rgba(255,255,255,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 16, color: 'rgba(255,255,255,0.15)',
                        }}>+</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', fontWeight: 600, letterSpacing: 1 }}>SIT HERE</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.1)', letterSpacing: 1 }}>EMPTY</div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Username */}
                    <div style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.5px',
                      color: isMe ? BJ_ACCENT : '#aaa',
                      maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      marginTop: 8,
                    }}>
                      {isMe ? 'YOU' : seat.username}
                    </div>

                    {/* Token type badge */}
                    <div style={{ fontSize: 10, color: '#666' }}>
                      {seat.tokenType === 'crewmate' ? '🔵' : '🔴'}
                    </div>

                    {/* Bet amount */}
                    {seatBet !== undefined && seatBet !== null && (
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: '#FFD700', letterSpacing: 0.5,
                        background: 'rgba(255,215,0,0.08)', padding: '2px 8px', borderRadius: 6,
                      }}>
                        BET: {fmtTokens(seatBet)}
                      </div>
                    )}

                    {/* Hands */}
                    {seatHands && seatHands.map((hand, hIdx) => (
                      <div key={hIdx} style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                        padding: '2px 0',
                        opacity: isActive && table.currentHandIndex !== hIdx ? 0.5 : 1,
                        border: isActive && table.currentHandIndex === hIdx ? '1px solid rgba(255,215,0,0.3)' : 'none',
                        borderRadius: 6, transition: 'opacity 0.2s',
                      }}>
                        <HandDisplay cards={hand.cards} small />
                        <div style={{
                          fontSize: 12, fontWeight: 800,
                          color: handValueNumeric(hand.cards) > 21 ? '#E8384A' : '#fff',
                        }}>
                          {handValueDisplay(hand.cards)}
                          {hand.doubled && <span style={{ fontSize: 9, color: '#9B59B6', marginLeft: 4 }}>2×</span>}
                          {hand.standing && handValueNumeric(hand.cards) <= 21 && <span style={{ fontSize: 9, color: '#888', marginLeft: 4 }}>STAND</span>}
                        </div>
                      </div>
                    ))}

                    {/* Result overlay — only after dealer cards fully revealed */}
                    {resultShown && seatResult && (
                      <div style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                        marginTop: 4, padding: '4px 8px', borderRadius: 8,
                        background: seatResult.netResult > 0 ? 'rgba(0,230,118,0.06)' : seatResult.netResult < 0 ? 'rgba(232,56,74,0.06)' : 'rgba(255,215,0,0.06)',
                      }}>
                        {seatResult.outcomes.map((outcome, oIdx) => (
                          <div key={oIdx} style={{
                            fontSize: 11, fontWeight: 800, letterSpacing: 0.8,
                            color: outcome === 'blackjack' ? '#FFD700'
                              : outcome === 'win' ? '#4ADE80'
                              : outcome === 'push' ? '#FBBF24'
                              : '#F87171',
                            textTransform: 'uppercase',
                          }}>
                            {outcome === 'blackjack' ? 'BLACKJACK!' : outcome.toUpperCase()}
                            {seatResult.handPayouts[oIdx] > 0 && (
                              <span style={{ color: '#4ADE80', marginLeft: 4, fontSize: 10 }}>
                                +{fmtTokens(seatResult.handPayouts[oIdx])}
                              </span>
                            )}
                          </div>
                        ))}
                        {seatResult.netResult !== 0 && (
                          <div style={{
                            fontSize: 13, fontWeight: 800, marginTop: 1,
                            color: seatResult.netResult > 0 ? '#4ADE80' : '#F87171',
                          }}>
                            {seatResult.netResult > 0 ? '+' : ''}{fmtTokens(seatResult.netResult)}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Controls Area ────────────────────────────────────────────────── */}

        {/* Join controls (when not seated) */}
        {!isJoined && !loading && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            padding: isMobile ? '12px 8px' : '16px 20px',
            background: 'rgba(0,0,0,0.3)', borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.05)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#888', letterSpacing: 2 }}>CHOOSE TOKEN TYPE TO JOIN</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setTokenType('crewmate')} style={S.tokenToggle(tokenType === 'crewmate')}>🔵 Crewmate</button>
              <button onClick={() => setTokenType('imposter')} style={S.tokenToggle(tokenType === 'imposter')}>🔴 Imposter</button>
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>Click an empty seat above to join</div>
          </div>
        )}

        {/* Betting controls (when seated and in betting phase) */}
        {isJoined && phase === 'betting' && !hasBet && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            padding: isMobile ? '12px 8px' : '16px 20px',
            background: 'rgba(0,0,0,0.3)', borderRadius: 16,
            border: `1px solid ${BJ_ACCENT_BORDER}`,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#FFD700', letterSpacing: 2 }}>PLACE YOUR BET</div>

            {/* Bet input row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setBetAmount(prev => Math.max(0.1, parseFloat((parseFloat(prev) / 2).toFixed(2))))} style={S.quickBet}>½</button>
              <input
                type="number"
                value={betAmount}
                onChange={e => setBetAmount(e.target.value)}
                onBlur={e => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isFinite(v) || v < 0.1) setBetAmount(0.1);
                  else setBetAmount(Math.min(10000, parseFloat(v.toFixed(2))));
                }}
                min="0.1" step="0.1"
                disabled={actionPending}
                style={S.betInput}
              />
              <button onClick={() => setBetAmount(prev => Math.min(10000, parseFloat((parseFloat(prev) * 2).toFixed(2))))} style={S.quickBet}>2×</button>
              <button onClick={() => setBetAmount(Math.min(10000, parseFloat(balance.toFixed(2))))} style={{ ...S.quickBet, color: '#E8384A' }}>MAX</button>
            </div>

            {/* Quick presets */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              {[0.1, 0.5, 1, 5, 10, 25, 50, 100].map(v => (
                <button key={v} onClick={() => setBetAmount(v)} style={{
                  ...S.quickBet,
                  background: parseFloat(betAmount) === v ? `${BJ_ACCENT_DIM}` : 'rgba(255,255,255,0.03)',
                  borderColor: parseFloat(betAmount) === v ? BJ_ACCENT_BORDER : 'rgba(255,255,255,0.06)',
                  color: parseFloat(betAmount) === v ? BJ_ACCENT : '#666',
                }}>{v}</button>
              ))}
            </div>

            <button
              onClick={handlePlaceBet}
              disabled={actionPending}
              style={{
                padding: isMobile ? '14px 40px' : '12px 40px',
                borderRadius: 12, cursor: actionPending ? 'default' : 'pointer',
                fontSize: 15, fontWeight: 800, letterSpacing: 2,
                background: `linear-gradient(135deg, ${BJ_ACCENT}44, ${BJ_ACCENT}22)`,
                border: `2px solid ${BJ_ACCENT_BORDER}`,
                color: BJ_ACCENT, textTransform: 'uppercase',
                boxShadow: `0 4px 20px ${BJ_ACCENT_DIM}`,
                transition: 'all 0.2s',
                opacity: actionPending ? 0.5 : 1,
              }}
            >
              DEAL — {fmtTokens(parseFloat(betAmount) || 0)} {mySeat?.tokenType === 'crewmate' ? '🔵' : '🔴'}
            </button>
          </div>
        )}

        {/* Waiting for bet confirmation */}
        {isJoined && phase === 'betting' && hasBet && (
          <div style={{
            textAlign: 'center', padding: '16px',
            background: 'rgba(0,0,0,0.3)', borderRadius: 16,
            border: `1px solid ${BJ_ACCENT_BORDER}`,
          }}>
            <div style={{ fontSize: 13, color: '#00E676', fontWeight: 700 }}>Bet placed: {fmtTokens(myBet)}</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>Waiting for other players...</div>
          </div>
        )}

        {/* Action buttons (when it's my turn) */}
        {isMyTurn && (
          <div style={{
            display: 'flex', gap: isMobile ? 8 : 10, justifyContent: 'center',
            padding: isMobile ? '8px 0' : '12px 0',
            flexWrap: 'wrap',
          }}>
            <button onClick={() => handleAction('hit')} disabled={!canHit || actionPending}
              style={S.actionBtn('#00E676', !canHit || actionPending)}>HIT</button>
            <button onClick={() => handleAction('stand')} disabled={!canStand || actionPending}
              style={S.actionBtn('#FF9800', !canStand || actionPending)}>STAND</button>
            <button onClick={() => handleAction('double')} disabled={!canDouble || actionPending}
              style={S.actionBtn('#9B59B6', !canDouble || actionPending)}>DOUBLE</button>
            <button onClick={() => handleAction('split')} disabled={!canSplit || actionPending}
              style={S.actionBtn(BJ_ACCENT, !canSplit || actionPending)}>SPLIT</button>
          </div>
        )}

        {/* Spectating message (seated but not my turn during playing) */}
        {isJoined && phase === 'playing' && !isMyTurn && (
          <div style={{ textAlign: 'center', padding: '10px', color: '#555', fontSize: 12, fontWeight: 600 }}>
            {table?.currentSeat >= 0 && seats[table.currentSeat]
              ? `${seats[table.currentSeat].username}'s turn...`
              : 'Waiting...'}
          </div>
        )}
      </div>
    </div>
  );
}

export default BlackjackPage;
